import _                                               from 'underscore';
import React, { FC, useContext }                       from 'react';
import moment, { Moment }                              from 'moment';
import { Divider, Header, Icon, List, Popup, Segment } from 'semantic-ui-react';
import { Cell, Grid }                                  from "styled-css-grid";

import { Align, CorvalProjectActivity, SemanticUIColor, Size } from '../../../../../types';
import { useFeatureFlags }                                     from '../../../../../hooks';
import { LangService, TimeService }                            from '../../../../../services';
import { ActivityQueries }                                     from '../../../../../queries/ActivityQueries';
import { ACTIVITY_STATUS_INFO, ENTITY_ICONS }                  from '../../../../../constants/ProductEnums';
import { ItemFromQuery }                                       from '../../../../components/from-query/ItemFromQuery';
import { FinePrintMessage }                                    from '../../../../components/util/messaging/FinePrintMessage';
import { TextLink }                                            from '../../../../components/display/TextLink';
import { StyledContainer }                                     from '../../../../containers';
import { InlineLoadingIndicatorLabel }                         from '../../../../components/util/loading/InlineLoadingIndicatorLabel';
import { VisualizationSummaryTile }                            from '../../common/visualization/display/VisualizationSummaryTile';
import { ProjectListItemCalendarItemLayout }                   from './ProjectListItemCalendarItemLayout';
import { GridColumnInfoType }                                  from './ProjectListItemCalendar';
import { DashboardProjectListItemContext }                     from './ProjectListItem';
import { maybeStaticVersionField }                             from '../../../../../util';
import { queriesHaveData }                                     from '../../../../../store/_apolloUtil';
import { DATES }                                               from "@src/constants/FormatStringEnums";


type ItemSetByDate = [ startDate: Moment, activitieSet: CorvalProjectActivity[] ];

interface Props {
    popupItemCount?: number,
    gridColumnCount: number,
    gridColumns: GridColumnInfoType[],
}

const defaultProps: Props = {
    popupItemCount:  5,
    gridColumnCount: 0,
    gridColumns:     [],
}

const ProjectListItemActivitiesCalendar: FC<Props> = ( props ) => {

    let { gridColumns, popupItemCount }            = props,
        { project, operations, updateProjectStat } = useContext( DashboardProjectListItemContext ),
        featureFlags                               = useFeatureFlags();

    let showCriticalityInfo = featureFlags.enableProjectActivityCriticalityViewsAndTools,
        termDictionary      = LangService.getDictionary( project.termDictionary );

    if( !gridColumns || _.isEmpty( gridColumns ) ) return null;

    let [ startTime ] = _.first( gridColumns ),
        [ , endTime ] = _.last( gridColumns );

    return (

        <ItemFromQuery
            noItemValid={ true }
            query={ ActivityQueries.queries.GET_PROJECT_ACTIVITIES( `
				id
				startTimeUtc
				endTimeUtc
				isRecurrence
				displayName
				completionState
				activity{ id defaultCriticality }
			` ) }
            queryOptions={ {
                variables:   {
                    projectId:                   project.id,
                    includeGeneratedRecurrences: true,
                    startTimeUtc:                TimeService.momentToISO( startTime ),
                    endTimeUtc:                  TimeService.momentToISO( endTime ),
                },
                fetchPolicy: 'cache-and-network'
            } }
            queryResultObject={ ( data ) => {

                if( !data ) return [];

                const projectActivitiesField = maybeStaticVersionField( 'projectActivites', !project.isCorvalManaged )

                return _.chain( data[ projectActivitiesField ] ?? [] )
                        .map( item => {

                            return ( {
                                ...item,
                                startDate: TimeService.ISOtoFormat( item.startTimeUtc, DATES.YEARMONTHDAY_MOMENT ),
                                endDate:   TimeService.ISOtoFormat( item.endTimeUtc, DATES.YEARMONTHDAY_MOMENT )
                            } )
                        } )
                        .groupBy( item => item.startDate )
                        .pairs()
                        .sortBy( ( [ startDate, ] ) => startDate )
                        .map( ( [ startDate, itemSet ] ) => ( [ moment( startDate, DATES.YEARMONTHDAY_MOMENT ), itemSet ] as ItemSetByDate ) )
                        .value();
            } }
            onQueryComplete={ ( request, resultingDataObject ) => {

                if( queriesHaveData( request ) ){

                    const projectActivitiesField = maybeStaticVersionField( 'projectActivites', !project.isCorvalManaged )

                    updateProjectStat( { type: 'activityCount', value: request.data[ projectActivitiesField ].length } );
                }

            } }
            loadingComponent={ () => {

                return (
                    <Segment color={ SemanticUIColor.TEAL } textAlign={ 'center' } basic>

                        <InlineLoadingIndicatorLabel label={ 'Retrieving Activity Schedule...' }/>

                    </Segment>
                )
            } }>

            { ( activitySetsByDate: ItemSetByDate[] ) => (

                <ProjectListItemCalendarItemLayout
                    items={ activitySetsByDate }
                    itemRender={ ( [ timeMarker, activitySet ] ) => (

                        <Popup hoverable
                               wide={ true }
                               position={ 'top center' }
                               trigger={

                                   <StyledContainer className={ 'cursor--pointer' } onClick={ () => operations.viewMap.callback(
                                       TimeService.asDisplayDate( timeMarker.clone().startOf( 'quarter' ) )
                                   ) }>

                                       <VisualizationSummaryTile
                                           bgColor={ SemanticUIColor.TEAL }
                                           metricValue={ activitySet.length }
                                           labelValue={ timeMarker.format( 'M/D' ) }
                                           gridProps={ { columnGap: '0px' } }
                                       />

                                   </StyledContainer>
                               }>

                            <Popup.Content className={ 'text-left' }>

                                <Header as={ 'h5' } className={ 'no-margin-bottom' }>Upcoming Activities</Header>
                                <Header.Subheader className={ 'text--colored-info' }>Starting { timeMarker.format( 'MMM. Do YYYY [(Q]Q[)]' ) }</Header.Subheader>

                                <Divider/>

                                <List size={ 'small' } relaxed divided>

                                    { _.first( activitySet, popupItemCount ).map( ( item, index ) => {

                                        let { id, displayName, activity: { defaultCriticality }, isRecurrence } = item,
                                            { ICON: statusIcon, COLOR: statusColor, LABEL: statusLabel }        = ACTIVITY_STATUS_INFO[ item.completionState ];

                                        return (

                                            <List.Item key={ `${ id }-${ index }` }>
                                                <List.Icon name={ statusIcon } color={ statusColor as SemanticUIColor }/>
                                                <List.Content>
                                                    <Grid columns={ `1fr 60px` } rowGap={ '0px' }>

                                                        {/* Activity Name */ }
                                                        <Cell>{ displayName }</Cell>

                                                        {/* Status Label */ }
                                                        <Cell className={ `text--colored-${ statusColor } text--xs text--strong text-right` }>
                                                            { LangService.getTermFromDictionary( statusLabel, termDictionary ) }
                                                        </Cell>

                                                        {/* Criticality */ }
                                                        {/*<Cell>{ showCriticalityInfo && <ActivityCriticalityLabel size={ 'mini' } value={ defaultCriticality as unknown as ACTIVITY_CRITICALITY }/> }</Cell>*/ }

                                                        {/* Recurrence Label */ }
                                                        { isRecurrence && (
                                                            <Cell>
                                                                <FinePrintMessage className={ 'text--sm' }><Icon name={ ENTITY_ICONS.ACTIVITIES.RECURRENCE } color={ 'grey' } size={ 'small' }/> Recurring Activity</FinePrintMessage>
                                                            </Cell>
                                                        ) }
                                                    </Grid>
                                                </List.Content>
                                            </List.Item>
                                        )
                                    } ) }

                                    { popupItemCount && ( activitySet.length > popupItemCount ) && (
                                        <List.Item>
                                            <StyledContainer textAlign={ Align.CENTER } padding={ [ Size.SM, null ] }>
                                                <TextLink onClick={ operations.viewMap.callback }>View All Activities</TextLink>
                                            </StyledContainer>
                                        </List.Item>
                                    ) }

                                </List>

                            </Popup.Content>

                        </Popup>

                    ) }
                    itemDisplayOffsetPX={ -10 }
                    itemMinWidth={ 55 }
                    getItemDate={ ( [ timeMarker, ] ) => timeMarker }
                    containerColor={ SemanticUIColor.TEAL }
                    containerProps={ { style: { padding: '0 40px 0 0' } } }
                    gridColumnCount={ props.gridColumnCount }
                    gridColumns={ props.gridColumns }
                    emptyComponent={ ( gridColumnInfo ) => (

                        <StyledContainer as={ 'p' } padding={ [ Size.SM, null ] } textAlign={ Align.CENTER }>

                            <FinePrintMessage as={ 'span' }>No upcoming activities. <TextLink onClick={ operations.viewMap.callback }>{ operations.viewMap.label }</TextLink></FinePrintMessage>

                        </StyledContainer>
                    ) }
                />

            ) }
        </ItemFromQuery>
    );
};

ProjectListItemActivitiesCalendar.defaultProps = defaultProps;

export { ProjectListItemActivitiesCalendar }
